import LabeledFillRows from "../components/LabeledFillRows";
import RuledTable from "../components/RuledTable";
import SectionHeading from "../components/SectionHeading";
import WhiteLabelCard from "../components/WhiteLabelCard";
import WhiteLabelPage from "../WhiteLabelPage/WhiteLabelPage";
import { WhiteLabelPageProps } from "../types";

import classes from "./MovementPlanPage.module.css";

const YOGA_ROWS = 8;
/** Fixed pitch keeps the 4 activity rows tight at the top, per the mock-up. */
const ACTIVITY_ROW_H = 44;

const MovementPlanPage = ({
  theme,
  lang,
  t,
  pageNumber,
}: WhiteLabelPageProps): JSX.Element => {
  return (
    <WhiteLabelPage
      theme={theme}
      lang={lang}
      brandLabelTemplate={t.footer.brandLabel}
      sectionName={t.sections.movementPlan}
      pageNumber={pageNumber}
      dataAgentTarget="wl-movement-plan"
    >
      <div className={classes.pageTitle}>{t.movement.heading}</div>
      <div className={classes.pageSubtitle}>{t.movement.subtitle}</div>

      <div className={classes.body}>
        {/* Yoga gets the top half — the client's stated focus. */}
        <div className={classes.half}>
          <div className={classes.sectionHeadingRow}>
            <SectionHeading
              label={t.movement.yoga.label}
              color={theme.sectionMarkers.yoga}
              plain
            />
          </div>
          <div className={classes.yogaHint}>{t.movement.yoga.hint}</div>
          <WhiteLabelCard fill>
            <RuledTable
              rows={YOGA_ROWS}
              columns={[
                { label: t.movement.yoga.columns.posture, flex: 1.5 },
                { label: t.movement.yoga.columns.reps, flex: 1 },
                { label: t.movement.yoga.columns.notes, flex: 1.5 },
              ]}
            />
          </WhiteLabelCard>
        </div>

        <div className={classes.half}>
          <div className={classes.columns}>
            <div className={classes.column}>
              <WhiteLabelCard>
                <div className={classes.cardHeading}>
                  <SectionHeading
                    label={t.movement.aerobic.label}
                    color={theme.sectionMarkers.aerobic}
                  />
                </div>
                <LabeledFillRows
                  rowHeight={ACTIVITY_ROW_H}
                  labels={[
                    t.movement.aerobic.rows.briskWalking,
                    t.movement.aerobic.rows.swimming,
                    t.movement.aerobic.rows.cycling,
                    t.movement.aerobic.rows.other,
                  ]}
                />
              </WhiteLabelCard>
            </div>

            <div className={classes.column}>
              <WhiteLabelCard>
                <div className={classes.cardHeading}>
                  <SectionHeading
                    label={t.movement.strength.label}
                    color={theme.sectionMarkers.strength}
                  />
                </div>
                <LabeledFillRows
                  rowHeight={ACTIVITY_ROW_H}
                  labels={[
                    t.movement.strength.rows.floorExercises,
                    t.movement.strength.rows.calisthenics,
                    t.movement.strength.rows.freeWeights,
                    t.movement.strength.rows.other,
                  ]}
                />
              </WhiteLabelCard>
            </div>
          </div>
        </div>
      </div>

      {/*
        The QR and its caption both come from the theme/dictionary, never the
        payload: OVERVIEW.QRCODE encodes https://pnoe.com/blog, which would send an
        AyurMa patient to PNOE's own site. Rendered inline rather than via the
        shared Qrcode component, which hardcodes its card margins and reads
        `sources.length` unguarded.
      */}
      <div className={classes.qrRow}>
        <img
          className={classes.qrImg}
          src={theme.qrCode}
          alt={`${theme.companyName} QR code`}
        />
        <div className={classes.qrCaption}>{t.qr.caption}</div>
      </div>
    </WhiteLabelPage>
  );
};

export default MovementPlanPage;
